import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Phone, CalendarClock, Wallet, ArrowUpRight, CheckCircle2, Circle } from "lucide-react";
import { api } from "../../lib/api";
import RevenueCard from "../../components/admin/RevenueCard";

const CARDS = [
  { key: "active_clients", label: "Clientes actives", sub: "En accompagnement 90 jours", icon: Users, fmt: (v) => v },
  { key: "upcoming_calls", label: "Appels à venir", sub: "Cette semaine", icon: Phone, fmt: (v) => v },
  { key: "upcoming_payments", label: "Paiements à venir", sub: "30 prochains jours", icon: CalendarClock, fmt: (v) => v },
  { key: "outstanding_balance", label: "Reste à encaisser", sub: "Sur les accompagnements", icon: Wallet, fmt: (v) => `${Number(v).toLocaleString("fr-FR")} €` },
];

const FILTERS = [
  { key: "a_faire", label: "À faire" },
  { key: "semaine", label: "Cette semaine" },
  { key: "termine", label: "Terminées" },
];

function today() {
  return new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function withinWeek(due) {
  if (!due) return false;
  const d = new Date(due);
  if (isNaN(d)) return false;
  const now = new Date();
  const in7 = new Date();
  in7.setDate(now.getDate() + 7);
  return d >= now && d <= in7;
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("a_faire");
  const navigate = useNavigate();

  const load = () => api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleTask = async (t) => {
    const status = t.status === "termine" ? "a_faire" : "termine";
    await api.put(`/tasks/${t.id}/status`, { status });
    load();
  };

  if (!data) return <div className="text-ink-muted">Chargement…</div>;

  const tasks = data.priorities || [];
  const filtered = tasks.filter((t) => {
    if (filter === "termine") return t.status === "termine";
    if (filter === "semaine") return t.status !== "termine" && withinWeek(t.due_date);
    return t.status !== "termine"; // à faire
  });

  return (
    <div className="max-w-6xl space-y-12">
      {/* Greeting */}
      <div className="animate-fade-up">
        <p className="eyebrow">{today()}</p>
        <h1 className="mt-2 font-sans text-3xl font-medium text-ink sm:text-4xl">{data.greeting}</h1>
      </div>

      {/* Stat cards */}
      <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
        {CARDS.map((card, i) => {
          const Icon = card.icon;
          const value = data.stats[card.key];
          return (
            <motion.button
              key={card.key}
              data-testid={`stat-card-${card.key}`}
              onClick={() => navigate("/admin/clientes")}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="group rounded-2xl border border-line bg-white p-5 text-left shadow-card transition hover:border-bordeaux-border hover:shadow-editorial"
            >
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-bordeaux-tint text-bordeaux">
                  <Icon size={18} />
                </span>
                <ArrowUpRight size={16} className="text-ink-muted transition group-hover:text-bordeaux" />
              </div>
              <p className="mt-4 font-sans text-3xl font-medium text-ink">{card.fmt(value)}</p>
              <p className="mt-1 text-sm font-medium text-ink">{card.label}</p>
              <p className="text-xs font-light text-ink-muted">{card.sub}</p>
            </motion.button>
          );
        })}
      </div>

      <RevenueCard />

      {/* Mes tâches */}
      <section data-testid="admin-tasks-card" className="max-w-3xl rounded-[28px] p-6 text-white shadow-[0_16px_48px_rgba(45,24,30,0.25)] sm:p-8" style={{ backgroundColor: "#2D181E", backgroundImage: "radial-gradient(circle at 100% 0%, rgba(123,17,58,0.55), transparent 60%)" }}>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-sans text-2xl font-medium text-white">Mes tâches</h2>
          <span className="rounded-full bg-white/10 px-3 py-1 text-xs text-white/70">{tasks.filter((t) => t.status !== "termine").length} à faire</span>
        </div>
        <div className="mb-5 flex gap-1 rounded-full bg-white/5 p-1.5 text-sm ring-1 ring-white/10">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`task-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`flex-1 whitespace-nowrap rounded-full px-3 py-2 font-sans text-xs transition-colors duration-200 sm:px-4 sm:text-sm ${
                filter === f.key ? "bg-bordeaux font-medium text-white shadow-[0_0_20px_rgba(123,17,58,0.6)]" : "text-white/70 hover:bg-white/5 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="space-y-2.5">
          {filtered.length === 0 && (
            <p className="rounded-2xl border border-white/10 bg-white/5 p-6 font-light text-white/60">
              {filter === "termine" ? "Aucune tâche terminée pour l'instant." : filter === "semaine" ? "Rien de prévu cette semaine." : "Rien en attente. Profite-en ✨"}
            </p>
          )}
          {filtered.map((t) => (
            <div
              key={t.id}
              data-testid={`task-item-${t.id}`}
              className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 transition-colors hover:bg-white/10"
            >
              <button data-testid={`task-toggle-${t.id}`} onClick={() => toggleTask(t)} className="text-[#F3D2D9]">
                {t.status === "termine" ? <CheckCircle2 size={22} /> : <Circle size={22} className="text-white/40" />}
              </button>
              <div className="flex-1">
                <p className={`text-sm ${t.status === "termine" ? "text-white/40 line-through" : "text-white"}`}>{t.title}</p>
                <span className="text-xs uppercase tracking-wider text-white/45">{t.source}{t.client_name ? ` · ${t.client_name}` : ""}</span>
                {/^https?:\/\//.test(t.link || "") && <a data-testid={`task-link-${t.id}`} href={t.link} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs text-[#F3D2D9] underline-offset-2 hover:underline">{t.source === "closing" ? "Voir l'appel ↗" : "Voir sa page ↗"}</a>}
              </div>
              {t.client_id && (
                <button
                  onClick={() => navigate(`/admin/clientes/${t.client_id}`)}
                  className="rounded-full bg-bordeaux px-3.5 py-1.5 text-xs font-medium text-white shadow-[0_0_14px_rgba(123,17,58,0.5)] transition-transform hover:-translate-y-0.5"
                >
                  Ouvrir
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
